import { useMemo, useState } from 'react';
import useEnergy from '../../../hooks/useEnergy';
import { contentService } from '../../../services';
import storage from '../../../lib/storage';
import manualEditorService from '../../../domain/manual-editor/services/manualEditorService';

const energyCosts = {
  post: 10,
  stories: 5,
  reels: 80,
  carrossel: 25
};

const mediaToContentType = {
  photo: 'post',
  video: 'reels',
  carousel: 'carrossel',
  design: 'post'
};

const contentTypeToMedia = {
  post: 'photo',
  stories: 'photo',
  reels: 'video',
  carrossel: 'carousel'
};

const profileToBusiness = {
  creator: 'creator',
  'saas-founder': 'services',
  'local-business': 'local'
};

const profileStrategyMap = {
  creator: ['authority', 'go-viral', 'sell-more'],
  'saas-founder': ['authority', 'sell-more'],
  'local-business': ['local-clients', 'sell-more']
};

const strategyToContentType = {
  'sell-more': 'post',
  authority: 'carrossel',
  'local-clients': 'stories',
  'go-viral': 'reels'
};

const profilePrompt = {
  creator: 'Perfil: creator focado em conteúdo de valor e engajamento.',
  'saas-founder': 'Perfil: fundador SaaS com foco em produto digital e geração de demanda.',
  'local-business': 'Perfil: empreendedor tradicional com atuação local e venda recorrente.'
};

const strategyPrompt = {
  'sell-more': 'Estratégia: vender mais com copy orientada para conversão.',
  authority: 'Estratégia: construir autoridade com conteúdo educativo e confiança.',
  'local-clients': 'Estratégia: atrair clientes locais com prova social e contexto regional.',
  'go-viral': 'Estratégia: viralizar com gancho forte e alto potencial de compartilhamento.'
};

export default function useFeedDomain() {
  const { userEnergy, setUserEnergy, referralCode } = useEnergy();

  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('creator');
  const [selectedStrategy, setSelectedStrategy] = useState('authority');
  const [selectedBusiness, setSelectedBusiness] = useState('ecommerce');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['instagram']);
  const [selectedContentType, setSelectedContentType] = useState('post');
  const [creativeMode, setCreativeMode] = useState('photo');
  const [creativeSize, setCreativeSize] = useState('1:1');
  const [creativeFormat, setCreativeFormat] = useState('standard');
  const [selectedFont, setSelectedFont] = useState({
    family: 'Inter',
    weight: '400',
    preview_url: '',
    font_class: 'font-inter',
  });
  const [selectedPhotoMedia, setSelectedPhotoMedia] = useState(null);
  const [selectedVideoMedia, setSelectedVideoMedia] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [overlayText, setOverlayText] = useState({ headline: '', subheadline: '', cta: '' });
  const [logoImage, setLogoImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [error, setError] = useState('');

  const canGenerate = useMemo(() => aiPrompt.trim().length > 0, [aiPrompt]);

  const togglePlatformSelection = (platformId) => {
    setSelectedPlatforms((previous) => {
      if (previous.includes(platformId)) {
        return previous.filter((id) => id !== platformId);
      }
      return [...previous, platformId];
    });
  };

  const buildSmartPrompt = (profileId, strategyId) => {
    const profileLine = profilePrompt[profileId] || '';
    const strategyLine = strategyPrompt[strategyId] || '';
    return `${profileLine}\n${strategyLine}\nObjetivo do post: `.trim();
  };

  const selectProfile = (profileId) => {
    const allowedStrategies = profileStrategyMap[profileId] || [];
    const fallbackStrategy = allowedStrategies[0] || selectedStrategy;
    const nextStrategy = allowedStrategies.includes(selectedStrategy) ? selectedStrategy : fallbackStrategy;

    setSelectedProfile(profileId);
    setSelectedStrategy(nextStrategy);
    setSelectedBusiness(profileToBusiness[profileId] || 'services');
    const nextContentType = strategyToContentType[nextStrategy] || 'post';
    setSelectedContentType(nextContentType);
    setCreativeMode(contentTypeToMedia[nextContentType] || 'photo');

    if (!aiPrompt.trim()) {
      setAiPrompt(buildSmartPrompt(profileId, nextStrategy));
    }
  };

  const selectStrategy = (strategyId) => {
    const allowedStrategies = profileStrategyMap[selectedProfile] || [];
    if (!allowedStrategies.includes(strategyId)) {
      return;
    }

    setSelectedStrategy(strategyId);
    const nextContentType = strategyToContentType[strategyId] || 'post';
    setSelectedContentType(nextContentType);
    setCreativeMode(contentTypeToMedia[nextContentType] || 'photo');

    if (!aiPrompt.trim()) {
      setAiPrompt(buildSmartPrompt(selectedProfile, strategyId));
    }
  };

  const handleCreativeModeChange = (mode) => {
    setCreativeMode(mode);
    setSelectedContentType(mediaToContentType[mode] || 'post');
  };

  const attachFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setAttachedFiles((previous) => {
      const next = [...previous];
      files.forEach((file) => {
        const alreadyExists = next.some(
          (item) => item.name === file.name && item.size === file.size && item.type === file.type
        );
        if (!alreadyExists) {
          next.push(file);
        }
      });
      return next.slice(0, 6);
    });
  };

  const removeAttachedFile = (indexToRemove) => {
    setAttachedFiles((previous) => previous.filter((_, index) => index !== indexToRemove));
  };

  const buildGenerationPrompt = () => {
    const modeLabel =
      creativeMode === 'video'
        ? 'vídeo'
        : creativeMode === 'carousel'
          ? 'carrossel'
          : creativeMode === 'design'
            ? 'design'
            : 'foto';

    const metadata = [
      `Tipo de criativo: ${modeLabel}`,
      `Formato: ${creativeFormat === 'premium' ? 'premium' : 'standard'}`,
      `Tamanho: ${creativeSize}`
    ];

    if (attachedFiles.length) {
      metadata.push(`Arquivos anexados: ${attachedFiles.map((file) => file.name).join(', ')}`);
    }

    return `${aiPrompt.trim()}\n\nConfigurações do criativo:\n- ${metadata.join('\n- ')}`;
  };

  const getSelectedMedia = () => {
    if (creativeMode === 'video') return selectedVideoMedia;
    return selectedPhotoMedia;
  };

  const generatePost = async () => {
    setError('');
    const cost = energyCosts[selectedContentType] || 10;

    if (!aiPrompt.trim()) {
      setError('Digite a descrição do conteúdo.');
      return;
    }

    if (userEnergy < cost) {
      setError(`Energia insuficiente. Convide amigos com seu código ${referralCode}.`);
      return;
    }

    setIsGenerating(true);

    try {
      const isDesignFlow = creativeMode === 'design' || creativeMode === 'carousel';
      const token = storage.get('authToken');
      const finalPrompt = buildGenerationPrompt();
      const selectedMedia = getSelectedMedia();
      const mediaUrl = selectedMedia?.media_url || selectedMedia?.large || selectedMedia?.original || selectedMedia?.medium || selectedMedia?.small || selectedMedia?.thumb || '';
      const mediaAuthor = selectedMedia?.media_author || selectedMedia?.author || '';
      const mediaType = selectedMedia?.type || (creativeMode === 'video' ? 'video' : 'photo');

      const generateLegacyContent = async () => {
        const endpoint = token ? '/posts/generate' : '/guest/posts/generate';
        const data = await contentService.generateByEndpoint(endpoint, {
          prompt: finalPrompt,
          platform: selectedPlatforms[0],
          platforms: selectedPlatforms,
          businessType: selectedBusiness,
          content_type: selectedContentType,
          creative_mode: creativeMode,
          creative_size: creativeSize,
          creative_format: creativeFormat,
          font_family: selectedFont.family,
          font_weight: selectedFont.weight,
          media_type: mediaType,
          media_url: mediaUrl,
          media_author: mediaAuthor,
          background_image_url: mediaType === 'photo' ? mediaUrl : '',
          background_image_author: mediaType === 'photo' ? mediaAuthor : '',
          tone: 'professional',
          image_text_options: overlayText,
          logo_image: logoImage
        }, { auth: Boolean(token) });

        if (!data?.success) {
          setError(data?.message || 'Falha ao gerar conteúdo.');
          return;
        }

        setGeneratedPost({
          ...data.post,
          kind: 'legacy-design',
        });

        if (data.energy?.remaining !== undefined) {
          setUserEnergy(data.energy.remaining);
        }
      };

      if (isDesignFlow) {
        const sessionId = token ? null : storage.get('guest_session_id');
        const designType = creativeMode === 'carousel' ? 'carrossel' : 'design';

        const data = await manualEditorService.generateDesign({
          prompt: finalPrompt,
          type: designType,
          size: creativeSize,
          platform: selectedPlatforms[0],
          format: creativeFormat,
          font_family: selectedFont.family,
          font_weight: selectedFont.weight,
          media_type: mediaType,
          media_url: mediaUrl,
          media_author: mediaAuthor,
          background_image_url: mediaType === 'photo' ? mediaUrl : '',
          background_image_author: mediaType === 'photo' ? mediaAuthor : '',
          session_id: sessionId,
          logo_mode: logoImage ? 'upload' : 'default'
        });

        if (!data?.success || !data?.design) {
          const message = String(data?.message || '').toLowerCase();
          const endpointMissing =
            !data ||
            message.includes('endpoint') ||
            message.includes('não encontrado') ||
            message.includes('nao encontrado') ||
            message.includes('not found');

          if (endpointMissing) {
            await generateLegacyContent();
            return;
          }

          setError(data?.message || 'Falha ao gerar design.');
          return;
        }

        setGeneratedPost({
          kind: 'design',
          design: data.design,
          content: data.design.prompt,
          image_url:
            (mediaType === 'photo' ? mediaUrl : '') ||
            data.design.media_url ||
            data.design.background_image_url ||
            data.design.images?.[0]?.url_regular ||
            data.design.images?.[0]?.url_full ||
            data.design.images?.[0]?.thumb || ''
        });
        return;
      }

      await generateLegacyContent();
    } catch (requestError) {
      console.error(requestError);
      setError('Erro de conexão durante a geração.');
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    aiPrompt,
    setAiPrompt,
    selectedProfile,
    selectedStrategy,
    selectProfile,
    selectStrategy,
    selectedBusiness,
    setSelectedBusiness,
    selectedPlatforms,
    togglePlatformSelection,
    selectedContentType,
    setSelectedContentType,
    creativeMode,
    creativeSize,
    creativeFormat,
    selectedFont,
    setSelectedFont,
    selectedPhotoMedia,
    setSelectedPhotoMedia,
    selectedVideoMedia,
    setSelectedVideoMedia,
    attachedFiles,
    setCreativeSize,
    setCreativeFormat,
    handleCreativeModeChange,
    attachFiles,
    removeAttachedFile,
    overlayText,
    setOverlayText,
    logoImage,
    setLogoImage,
    isGenerating,
    generatedPost,
    error,
    canGenerate,
    userEnergy,
    generatePost
  };
}
